import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SessionProvider, useSession } from "./auth";
import { Landing } from "./routes/Landing";
import { LearnerHome } from "./routes/LearnerHome";
import { PlayerView } from "./routes/PlayerView";
import { StatusPage } from "./routes/StatusPage";
import { TrackView } from "./routes/TrackView";
import { ProgrammesPage } from "./admin/ProgrammesPage";
import { ProgrammePage } from "./admin/ProgrammePage";
import { TrackPage } from "./admin/TrackPage";
import { ModulePage } from "./admin/ModulePage";
import { LearningItemPage } from "./admin/LearningItemPage";
import { LearnersPage } from "./admin/LearnersPage";
import { QuestionBankPage } from "./admin/QuestionBankPage";
import { CertificateTemplatesPage } from "./admin/CertificateTemplatesPage";
import { FeedbackTemplatesPage } from "./admin/FeedbackTemplatesPage";
import { ReportsPage } from "./admin/ReportsPage";
import { NotificationsPage } from "./admin/NotificationsPage";
import { AssessmentView } from "./routes/AssessmentView";
import { DiagnosticView } from "./routes/DiagnosticView";
import { Shell } from "./ui";

function Home() {
  const session = useSession();
  if (session.status === "loading") return <Shell title="Loading…">{null}</Shell>;
  if (session.status === "anonymous" || session.status === "unprovisioned") return <Landing />;
  return session.me.role === "SUPER_ADMIN" ? (
    <Navigate to="/admin" replace />
  ) : (
    <LearnerHome me={session.me} />
  );
}

function RequireRole({ role, children }: { role: "SUPER_ADMIN" | "LEARNER"; children: ReactNode }) {
  const session = useSession();
  if (session.status === "loading") return <Shell title="Loading…">{null}</Shell>;
  if (session.status !== "authed") return <Landing />;
  if (session.me.role !== role) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />

          <Route
            path="/admin"
            element={
              <RequireRole role="SUPER_ADMIN">
                <ProgrammesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/programmes/:id"
            element={
              <RequireRole role="SUPER_ADMIN">
                <ProgrammePage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/tracks/:id"
            element={
              <RequireRole role="SUPER_ADMIN">
                <TrackPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/modules/:id"
            element={
              <RequireRole role="SUPER_ADMIN">
                <ModulePage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/learning-items/:id"
            element={
              <RequireRole role="SUPER_ADMIN">
                <LearningItemPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/learners"
            element={
              <RequireRole role="SUPER_ADMIN">
                <LearnersPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/question-banks/:id"
            element={
              <RequireRole role="SUPER_ADMIN">
                <QuestionBankPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/certificates"
            element={
              <RequireRole role="SUPER_ADMIN">
                <CertificateTemplatesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/feedback-templates"
            element={
              <RequireRole role="SUPER_ADMIN">
                <FeedbackTemplatesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <RequireRole role="SUPER_ADMIN">
                <ReportsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <RequireRole role="SUPER_ADMIN">
                <NotificationsPage />
              </RequireRole>
            }
          />

          <Route
            path="/learn/tracks/:trackId"
            element={
              <RequireRole role="LEARNER">
                <TrackView />
              </RequireRole>
            }
          />
          <Route
            path="/learn/items/:itemId"
            element={
              <RequireRole role="LEARNER">
                <PlayerView />
              </RequireRole>
            }
          />
          <Route
            path="/learn/assessments/:trackId"
            element={
              <RequireRole role="LEARNER">
                <AssessmentView />
              </RequireRole>
            }
          />
          <Route
            path="/learn/diagnostics/:trackId/:phase"
            element={
              <RequireRole role="LEARNER">
                <DiagnosticView />
              </RequireRole>
            }
          />

          <Route path="/status" element={<StatusPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}
